import React, { useEffect, useState } from 'react'
import NoOrderPanelContainer from './NoOrderContainer'
import { ORDER_TYPES, useOrder } from 'context/OrderContext'
import { Box, useMediaQuery, useTheme } from '@mui/material'
import { useSelector } from 'react-redux'
import { useAddOrderMutation, useFindDeliveryFeeMutation } from 'pages/dashboard/services'
import { useGetSettingsQuery } from 'pages/settings/ManageSettings/services'
import OrderPanelContainer from './OrderPanelContainer'
import { getUTCDateTime, getUTCFromLocalTime } from 'utils/function'

const OrderPanel = () => {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"))
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
  const userData = useSelector((state) => state?.posData)

  const platformFeePercent = parseFloat(userData?.vendor?.platform_fee) || 0;
  const serviceTaxPercent = parseFloat(userData?.vendor?.service_tax) || 0;

  // Use the OrderContext
  const { orderType, orderItems, customerData, addItemToOrder, removeItemFromOrder, resetOrder, openDialog, closeDialog } = useOrder()

  // Add the mutation hook
  const [addOrder, { isLoading }] = useAddOrderMutation()
  const [findDeliveryFeeApi] = useFindDeliveryFeeMutation()
  const { data: settingsData } = useGetSettingsQuery()

  // State for payment mode
  const [paymentMode, setPaymentMode] = useState("cash")
  const [openCancelConfirm, setOpenCancelConfirm] = useState(false)

  // 2️⃣ Handlers to open/close the dialog
  const handleOpenCancel = () => setOpenCancelConfirm(true)
  const handleCloseCancel = () => setOpenCancelConfirm(false)

  // 3️⃣ When user confirms cancellation…
  const handleConfirmCancel = () => {
    // actually reset the order
    resetOrder()
    setSuccessDialogOpen(false)
    // close the dialog
    setOpenCancelConfirm(false)
  }
  // State for order success dialog
  const [successDialogOpen, setSuccessDialogOpen] = useState(false)
  const [orderID, setOrderID] = useState("")

  // Whenever a new order is started (orderType is set), ensure successDialogOpen is false
  useEffect(() => {
    if (orderType) {
      setSuccessDialogOpen(false);
    }
  }, [orderType]);

  // State for API calculated delivery fee
  const [apiDeliveryFee, setApiDeliveryFee] = useState(null)

  useEffect(() => {
    const fetchDeliveryFee = async () => {
      const lat = customerData?.lat || customerData?.latitude;
      const long = customerData?.long || customerData?.longitude;
      const vendorId = userData?.id;

      if ((orderType === ORDER_TYPES.DELIVERY || orderType === ORDER_TYPES.PHONE_ORDER) && lat && long && vendorId) {
        try {
          const payload = {
            customer_lat: lat.toString(),
            customer_long: long.toString(),
            vendor_id: vendorId,
          };
          console.log("OrderPanel calling Delivery Fee API Payload:", payload);
          const res = await findDeliveryFeeApi(payload).unwrap();
          console.log("OrderPanel Delivery Fee API Response:", res);
          if (res?.data && res?.data?.delivery_fee !== undefined) {
            setApiDeliveryFee(parseFloat(res.data.delivery_fee) || 0);
          }
        } catch (err) {
          console.error("OrderPanel error calculating delivery fee:", err);
        }
      } else {
        setApiDeliveryFee(null);
      }
    };

    fetchDeliveryFee();
  }, [customerData?.lat, customerData?.long, customerData?.latitude, customerData?.longitude, orderType, userData?.id]);

  // Calculate totals
  const cartTotal = orderItems.reduce((total, item) => {
    const itemTotal = item.price * item.quantity
    const addOnsTotal = item.addOns ? item.addOns.reduce((sum, addon) => sum + addon.price, 0) : 0
    return total + itemTotal + addOnsTotal * item.quantity
  }, 0)

  const settings = settingsData?.data?.settings;
  const isPackageChargeEnabled = settings?.package_charge_enabled === 1;
  const packageChargePrice = isPackageChargeEnabled ? parseFloat(settings?.package_charge_price) || 0 : 0;
  const packageCharge = (orderType === ORDER_TYPES.TAKE_AWAY || orderType === ORDER_TYPES.PHONE_ORDER) ? packageChargePrice : 0;
  const deliveryFee = (orderType === ORDER_TYPES.DELIVERY || orderType === ORDER_TYPES.PHONE_ORDER)
    ? (apiDeliveryFee !== null
      ? apiDeliveryFee
      : (parseFloat(customerData?.delivery_fee || settings?.delivery_fee || settings?.delivery_charge || 0) || 0))
    : 0;

  const tax = (cartTotal * serviceTaxPercent) / 100; // service tax
  const platformFee = 0; // POS orders: platform fee is absorbed by vendor, not charged to customer
  const orderTotal = cartTotal + tax + packageCharge + deliveryFee;
  // Format cart items for the API
  const formatCartItems = () => {
    return orderItems.map((item) => ({
      ...(item.isMisc ? {} : { id: item.id }),
      name: item.name,
      price: item.price ?? 0,
      image: item.image || null,
      // primary_chef_id: item.isMisc ? null : (item?.station?.chefs?.length > 1 ? null : item?.station?.chefs[0]?.id),
      primary_chef_id: item.isMisc
        ? (item.cookingRequired ? item.primary_chef_id : null)
        : (item?.station?.chefs?.length > 1 ? null : item?.station?.chefs[0]?.id),
      station_id: item.isMisc
        ? (item.cookingRequired ? (item.station_id || null) : null)
        : item?.station?.id,
      quantity: item.quantity,
      total_price: (item.price ?? 0) * item.quantity,
      ...(item.notes && { notes: item.notes, note: item.notes }),
      ...(item.isMisc && { isMiscellaneous: true }),
      ...(item.isMisc && item.cookingRequired && { cookingRequired: true }),
      ...(item.addOns &&
        item.addOns.length > 0 && {
        add_ons: item.addOns.map((addon) => ({
          id: addon.id || Math.floor(Math.random() * 1000), // Fallback if id is missing
          name: addon.name,
          price: addon.price,
        })),
      }),
    }))
  }
  // Reset Order ONLY when the POS page is opened
  // useEffect(() => {
  //   // Do not reset if showing success dialog
  //   if (!successDialogOpen) {
  //     resetOrder();
  //   }
  // }, []);

  // Handle create order with API integration
  const handleCreateOrder = async () => {
    try {
      let booking_date, booking_time;

      if (orderType === ORDER_TYPES.TABLE_BOOKING) {
        // convert user‐inputs into UTC
        const { utcDate, utcTime } = getUTCDateTime(
          customerData?.day,
          customerData?.time
        );
        booking_date = utcDate;
        booking_time = utcTime;
      } else {
        // for TAKE_AWAY or DINE_IN, just send "now" in UTC
        const nowIso = new Date().toISOString();  // e.g. "2025-07-14T10:30:45.123Z"
        booking_date = nowIso.split('T')[0];      // "2025-07-14"
        booking_time = nowIso.split('T')[1].split('.')[0]; // "10:30:45"
      }

      let end_time = "";
      if (orderType === ORDER_TYPES.TABLE_BOOKING && customerData?.end_time) {
        // end_time is already in UTC format from OrderDialog, no conversion needed
        end_time = customerData.end_time;
      }
      console.log(end_time, "end_time submittt==>>>")
      // Prepare order data according to backend requirements
      const orderData = {
        customer_id: customerData?.id || null, // Use customer ID from context or default
        order_source: "pos",
        order_type:
          orderType === ORDER_TYPES.TAKE_AWAY ? "pos"
            : orderType === ORDER_TYPES.DINE_IN ? "dine_in"
              : orderType === ORDER_TYPES.PHONE_ORDER ? "phone_order"
                : "booking",
        ...(orderType === ORDER_TYPES.PHONE_ORDER && customerData?.customer_address
          ? {
            customer_address: customerData.customer_address,
            building_number: customerData?.building_number || null,
            building_name: customerData?.building_name || null,
            lat: customerData?.lat || null,
            long: customerData?.long || null,
            // latitude: customerData?.lat || customerData?.latitude || null,
            // longitude: customerData?.long || customerData?.longitude || null,
            city: customerData?.city || null,
            state: customerData?.state || null,
            country: customerData?.country || null,
            postal_code: customerData?.postal_code || null,
            // address_details: customerData?.address_details || null,
          }
          : {}),
        table_id: customerData?.table || null,
        // booking_date:
        //   orderType === ORDER_TYPES.TABLE_BOOKING
        //     // ? customerData?.day || new Date().toISOString().split("T")[0]
        //    ? parseBookingDate(customerData?.day)
        //     : new Date().toISOString().split("T")[0],
        // booking_time:
        //   orderType === ORDER_TYPES.TABLE_BOOKING
        //     // ? customerData?.time || "18:00"
        //     ? parseBookingTime(customerData?.time)
        //     : new Date().toTimeString().split(" ")[0].substring(0, 5),
        booking_date,
        booking_time,
        end_time,
        booking_slot:
          orderType === ORDER_TYPES.TABLE_BOOKING ? customerData?.time_slot?.toLowerCase() || "dinner" : null,
        guest_count: customerData?.persons || null,
        tax_amount: serviceTaxPercent,
        platform_fee: 0, // POS order: fee absorbed by vendor
        discount_amount: 0,
        order_items_total: cartTotal,
        package_charge: packageCharge,
        delivery_fee: deliveryFee,
        total_amount: orderTotal,
        cart: formatCartItems(),
      }

      console.log("Full Order Payload sending to DB API:", orderData);

      // Call the API to create the order
      const response = await addOrder(orderData).unwrap()
      setOrderID(response?.data?.order_id)

      // Show success dialog (resetOrder is called when dialog auto-closes)
      setSuccessDialogOpen(true)
    } catch (error) {
      console.error("Failed to create order:", error)
      // You might want to show an error message to the user here
    }
  }

  // Handle item quantity change
  const handleAddItem = (item) => {
    // Create a copy of the item with quantity set to 1
    // This ensures we only increment by 1 regardless of the item's current quantity
    const itemToAdd = {
      ...item,
      quantity: 1,
    }
    addItemToOrder(itemToAdd)
  }

  const handleRemoveItem = (item) => {
    removeItemFromOrder(item.id, item.addOns, item.size)
  }


  return (
    <>
      {orderType ?
        <OrderPanelContainer
          handleRemoveItem={handleRemoveItem}
          handleAddItem={handleAddItem}
          handleCreateOrder={handleCreateOrder}
          successDialogOpen={successDialogOpen}
          orderID={orderID}
          handleConfirmCancel={handleConfirmCancel}
          handleOpenCancel={handleOpenCancel}
          paymentMode={paymentMode}
          openCancelConfirm={openCancelConfirm}
          setPaymentMode={setPaymentMode}
          handleCloseCancel={handleCloseCancel}
          userData={userData}
          isLoading={isLoading}
          theme={theme}
          orderType={orderType}
          customerData={customerData}
          orderItems={orderItems}
          resetOrder={resetOrder}
          tax={tax}
          platformFee={platformFee}
          // discount={discount}
          orderTotal={orderTotal}
          cartTotal={cartTotal}
          packageCharge={packageCharge}
          deliveryFee={deliveryFee}
          setSuccessDialogOpen={setSuccessDialogOpen}
        />
        :
        <NoOrderPanelContainer
          openDialog={openDialog}
          closeDialog={closeDialog}
          isDesktop={isDesktop}
          isMobile={isMobile}
        />
      }
    </>
  )
}

export default OrderPanel
